import { useMemo } from "react";
import { useACLContext } from "../context";
import type { EvaluationResult } from "@shield-acl/core";
import type { PermissionCheck } from "./useCanAny";

/**
 * Resultado da verificação múltipla de permissões
 */
export interface MultiplePermissionResult<TContext = any> {
  /** Indica se TODAS as permissões foram permitidas */
  allAllowed: boolean;

  /** Indica se ALGUMA permissão foi permitida */
  anyAllowed: boolean;

  /** Indica se NENHUMA permissão foi permitida */
  noneAllowed: boolean;

  /** Contagem de permissões permitidas */
  allowedCount: number;

  /** Contagem de permissões negadas */
  deniedCount: number;

  /** Resultados individuais indexados por chave */
  results: Record<string, boolean>;

  /** Detalhes completos de avaliação indexados por chave */
  details: Record<string, EvaluationResult>;

  /** Lista de permissões permitidas */
  allowed: PermissionCheck<TContext>[];

  /** Lista de permissões negadas */
  denied: PermissionCheck<TContext>[];
}

/**
 * Hook que verifica múltiplas permissões e retorna um objeto detalhado com os resultados
 *
 * @param permissions - Array de permissões para verificar
 * @param keys - Array opcional de chaves customizadas para indexar resultados
 * @returns Objeto com resultados detalhados de todas as verificações
 *
 * @example
 * ```tsx
 * // Verificar múltiplas permissões com análise detalhada
 * const permissionCheck = useCanMultiple([
 *   { action: "posts.create" },
 *   { action: "posts.edit", resource: "posts" },
 *   { action: "posts.delete", resource: "posts" },
 *   { action: "users.manage", resource: "users" }
 * ])
 *
 * // Verificar status geral
 * if (permissionCheck.allAllowed) {
 *   console.log("Tem todas as permissões!")
 * } else if (permissionCheck.anyAllowed) {
 *   console.log(`Tem ${permissionCheck.allowedCount} de ${permissions.length} permissões`)
 * }
 *
 * // Verificar permissões específicas por índice
 * if (permissionCheck.results["posts.create"]) {
 *   // Pode criar posts
 * }
 *
 * // Com chaves customizadas
 * const namedCheck = useCanMultiple(
 *   [
 *     { action: "posts.create" },
 *     { action: "posts.edit", resource: "posts" }
 *   ],
 *   ["createPost", "editPost"] // Chaves customizadas
 * )
 *
 * if (namedCheck.results.createPost) {
 *   // Pode criar posts
 * }
 *
 * // Acessar detalhes da avaliação
 * console.log(namedCheck.details.editPost.reason)
 * ```
 */
export function useCanMultiple<TContext = any>(
  permissions: PermissionCheck<TContext>[],
  keys?: string[],
): MultiplePermissionResult<TContext> {
  const { can, evaluate, user } = useACLContext<TContext>();

  return useMemo(() => {
    const results: Record<string, boolean> = {};
    const details: Record<string, EvaluationResult> = {};
    const allowed: PermissionCheck<TContext>[] = [];
    const denied: PermissionCheck<TContext>[] = [];

    // Verifica cada permissão
    permissions.forEach((permission, index) => {
      // Gera chave para o resultado
      const key =
        keys?.[index] ||
        `${permission.action}${permission.resource ? `.${permission.resource}` : ""}`;

      // Avalia a permissão
      const evaluationResult = evaluate(
        permission.action,
        permission.resource,
        permission.context,
      );

      const isAllowed = evaluationResult.allowed;

      // Armazena resultados
      results[key] = isAllowed;
      details[key] = evaluationResult;

      // Categoriza a permissão
      if (isAllowed) {
        allowed.push(permission);
      } else {
        denied.push(permission);
      }
    });

    // Calcula estatísticas
    const allowedCount = allowed.length;
    const deniedCount = denied.length;
    const allAllowed = allowedCount === permissions.length;
    const anyAllowed = allowedCount > 0;
    const noneAllowed = allowedCount === 0;

    return {
      allAllowed,
      anyAllowed,
      noneAllowed,
      allowedCount,
      deniedCount,
      results,
      details,
      allowed,
      denied,
    };
  }, [can, evaluate, permissions, keys, user]);
}

/**
 * Hook simplificado que retorna apenas o mapa de resultados booleanos
 *
 * @param permissions - Array de permissões para verificar
 * @param keys - Array opcional de chaves customizadas
 * @returns Objeto com resultados booleanos indexados por chave
 *
 * @example
 * ```tsx
 * const canMap = useCanMap([
 *   { action: "posts.create" },
 *   { action: "posts.edit" },
 *   { action: "posts.delete" }
 * ])
 *
 * // { "posts.create": true, "posts.edit": true, "posts.delete": false }
 *
 * if (canMap["posts.create"] && canMap["posts.edit"]) {
 *   // Pode criar e editar
 * }
 * ```
 */
export function useCanMap<TContext = any>(
  permissions: PermissionCheck<TContext>[],
  keys?: string[],
): Record<string, boolean> {
  const { results } = useCanMultiple(permissions, keys);
  return results;
}

/**
 * Hook que verifica permissões e retorna array de booleanos na mesma ordem
 *
 * @param permissions - Array de permissões para verificar
 * @returns Array de booleanos na mesma ordem das permissões
 *
 * @example
 * ```tsx
 * const [canCreate, canEdit, canDelete] = useCanArray([
 *   { action: "posts.create" },
 *   { action: "posts.edit" },
 *   { action: "posts.delete" }
 * ])
 *
 * if (canCreate && canEdit) {
 *   // Pode criar e editar
 * }
 * ```
 */
export function useCanArray<TContext = any>(
  permissions: PermissionCheck<TContext>[],
): boolean[] {
  const { can, user } = useACLContext<TContext>();

  return useMemo(() => {
    return permissions.map((permission) =>
      can(permission.action, permission.resource, permission.context),
    );
  }, [can, permissions, user]);
}
